import React, { useState, useRef, useEffect } from 'react';
import { FiMessageSquare, FiX, FiSend, FiUser, FiMail } from 'react-icons/fi';
import chatService from '../../services/ChatService';

function AIChat() {
    const [isOpen, setIsOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');              
    const [emailInput, setEmailInput] = useState('');    
    const [showIntro, setShowIntro] = useState(true);
    const [emailPrompted, setEmailPrompted] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const startChat = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setShowIntro(false);
        setMessages([
            { role: 'assistant', content: `Hi ${name}! I'm Guardhart's AI assistant. How can I help you today?` }
        ]);
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMsg = input;
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setInput('');
        setLoading(true);

        try {
            const res = await chatService.sendMessage({
                session_id: sessionId,
                message: userMsg,
                name,
                email, 
            });
            setSessionId(res.data.session_id);
            if (res.data.email) setEmail(res.data.email);
            setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
        } catch (err) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "Sorry, I'm having trouble right now. Please call us at +254 117 666 000."
            }]);
        } finally {
            setLoading(false);
        }
    };

    const submitEmail = (e) => {
        e.preventDefault();
        if (!emailInput.trim()) return;

        setEmail(emailInput);
        setEmailPrompted(true);

        chatService.sendMessage({
            session_id: sessionId,
            message: '',
            name,
            email: emailInput,
        }).then(() => {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "Thanks! Our team will reach out if needed. Anything else I can help with?"
            }]);
            setEmailInput('');
        }).catch(() => {
            setEmailPrompted(true);
        });
    };

    const shouldPromptEmail = !email && !emailPrompted &&
        messages.some(m => m.role === 'assistant' && /email|reach out|follow up/i.test(m.content));

    return (
        <>
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-6 right-6 w-14 h-14 bg-[#FFC000] text-zinc-900 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center z-50"
                    aria-label="Open chat"
                >
                    <FiMessageSquare className="w-6 h-6" />
                </button>
            )}

            {isOpen && (
                <div className="fixed bottom-6 right-6 w-[360px] h-[560px] bg-white border border-zinc-200 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden">
                    <div className="bg-zinc-900 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-[#FFC000] flex items-center justify-center">
                                <FiMessageSquare className="w-4 h-4 text-zinc-900" />
                            </div>
                            <div>
                                <h3 className="font-bold text-white text-sm">Guardhart Assistant</h3>
                                <p className="text-[11px] text-zinc-400 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
                                    Online
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="w-8 h-8 flex items-center justify-center rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>

                    {showIntro ? (
                        <div className="flex-1 p-6 flex flex-col justify-center bg-white">
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 bg-[#FFC000]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FiUser className="w-7 h-7 text-[#FFC000]" />
                                </div>
                                <h4 className="text-lg font-bold text-zinc-900 mb-2">Welcome!</h4>
                                <p className="text-sm text-zinc-500">What's your name so we can get started?</p>
                            </div>
                            <form onSubmit={startChat} className="space-y-3">
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Your name"
                                    className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#FFC000] focus:ring-2 focus:ring-[#FFC000]/10"
                                    required
                                    autoFocus
                                />
                                <button
                                    type="submit"
                                    className="w-full bg-[#FFC000] text-zinc-900 px-6 py-2.5 text-xs font-bold uppercase tracking-widest rounded-lg hover:brightness-110 transition-all"
                                >
                                    Start Chat
                                </button>
                            </form>
                        </div>
                    ) : (
                        <>
                            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
                                {messages.map((msg, idx) => (
                                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.role === 'user'
                                            ? 'bg-[#FFC000] text-zinc-900 rounded-br-sm'
                                            : 'bg-white text-zinc-800 border border-zinc-200 rounded-bl-sm'
                                            }`}>
                                            {msg.content}
                                        </div>
                                    </div>
                                ))}
                                {loading && (
                                    <div className="flex justify-start">
                                        <div className="bg-white border border-zinc-200 px-4 py-3 rounded-2xl rounded-bl-sm">
                                            <div className="flex gap-1">
                                                <div className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce"></div>
                                                <div className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                                                <div className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {shouldPromptEmail && (
                                <form onSubmit={submitEmail} className="px-4 py-2 bg-white border-t border-zinc-200 flex gap-2">
                                    <FiMail className="w-4 h-4 text-[#FFC000] mt-2.5 shrink-0" />
                                    <input
                                        type="email"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        placeholder="Your email..."
                                        className="flex-1 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFC000]"
                                        required
                                    />
                                    <button type="submit" className="px-3 py-2 bg-[#FFC000] text-zinc-900 rounded-lg text-xs font-bold hover:brightness-110">
                                        Send
                                    </button>
                                </form>
                            )}

                            <form onSubmit={sendMessage} className="p-3 bg-white border-t border-zinc-200">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder="Ask about our services..."
                                        className="flex-1 bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#FFC000] focus:ring-2 focus:ring-[#FFC000]/10"
                                        disabled={loading}
                                    />
                                    <button
                                        type="submit"
                                        disabled={loading || !input.trim()}
                                        className="w-10 h-10 bg-[#FFC000] text-zinc-900 rounded-lg flex items-center justify-center hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <FiSend className="w-4 h-4" />
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            )}
        </>
    );
}

export default AIChat;